import { defineStore } from "pinia";
import {
    FetchState,
    MessageActionType,
    MessageCenterGetMessagesResponse,
    MessageCenterMessage,
    MessageCenterRequest,
    MessageCenterState,
    UpdateMessageRequest
} from "@/shared/types";
import * as advisorService from "@/shared/api/advisor3Service";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: MessageCenterState = {
    messagesFetchState: FetchState.Uninitialized,
    messageFetchState: FetchState.Uninitialized,
    messageUpdateFetchState: FetchState.Uninitialized,
    documentFetchState: FetchState.Uninitialized,
    messageCenterMessageResponse: {
        messages: [],
        pageNumber: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
        pageRange: [],
        totalRecords: 0
    },
    selectedMessage: null
};

export const useMessageCenterStore = defineStore("messageCenter", {
    state: (): MessageCenterState => (_.cloneDeep(initialState)),
    getters: {
        messagesAreLoaded: state => state.messagesFetchState === FetchState.Loaded,
        isDocumentDownloading: state => state.documentFetchState === FetchState.Loading,
    },
    actions: {
        setMessagesFetchState (fetchState: FetchState) {
            this.messagesFetchState = fetchState;
        },
        setMessageFetchState (fetchState: FetchState) {
            this.messageFetchState = fetchState;
        },
         setMessageUpdateFetchState (fetchState: FetchState) {
            this.messageUpdateFetchState = fetchState;
        },
        setDocumentFetchState (fetchState: FetchState) {
          this.documentFetchState = fetchState;
        },
        setMessages (
            response: MessageCenterGetMessagesResponse) {
            this.setMessagesFetchState(FetchState.Loaded);
            this.messageCenterMessageResponse = response;
            this.messageCenterMessageResponse.totalPages =   Math.ceil(response.total / response.pageSize);
            this.messageCenterMessageResponse.totalRecords =   response.total;
            response.pageRange = _.range(response.pageNumber - 2, response.pageNumber + 3)
                        .filter(page => page > 0 && page <= this.messageCenterMessageResponse.totalPages);
        },
        setMessage (
            message: MessageCenterMessage) {
            this.setMessageFetchState(FetchState.Loaded);
            this.selectedMessage = message;
            this.messageCenterMessageResponse.messages = this.messageCenterMessageResponse.messages.map(m => m.messageCenterMessageId === message.messageCenterMessageId ? message : m);
        },
        updateMessage (message: MessageCenterMessage) {
            this.setMessageUpdateFetchState(FetchState.Loaded);
            this.messageCenterMessageResponse.messages = this.messageCenterMessageResponse.messages.map(m => m.messageCenterMessageId === message.messageCenterMessageId ? message : m);
            if (this.selectedMessage?.messageCenterMessageId === message.messageCenterMessageId) {
                this.selectedMessage = message;
            }
        },
        updateMessages (message: MessageCenterMessage[]) {
            this.setMessageUpdateFetchState(FetchState.Loaded);
            message.forEach((mcm: MessageCenterMessage) => {
                this.messageCenterMessageResponse.messages = this.messageCenterMessageResponse.messages.map(m => m.messageCenterMessageId === mcm.messageCenterMessageId ? mcm : m);
                if (this.selectedMessage?.messageCenterMessageId === mcm.messageCenterMessageId) {
                    this.selectedMessage = mcm;
                }
            });
        },
        getDocumentUrl (messageCenterDocumentId: number): Promise<any> {
            this.setDocumentFetchState(FetchState.Loading);
            const newTab = window.open();
            return advisorService.getDocumentUrl(messageCenterDocumentId)
                .then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
                    newTab.close();
                })
                .finally(() => this.setDocumentFetchState(FetchState.Loaded));
        },
        getSelectedMessage ( id: string): Promise<any> {
            this.setMessageFetchState(FetchState.Loading);

            return advisorService.getMessage(id)
                .then(response => this.setMessage(response.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getFilteredMessages (request: MessageCenterRequest): Promise<any> {
            this.setMessagesFetchState(FetchState.Loading);

            return advisorService.getFilteredMessages(request)
                .then(response => this.setMessages(response.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        setMessageSavedOrArchived (request: UpdateMessageRequest[], actionType: MessageActionType): Promise<any> {
            const notifierStore = useNotifierStore();
            this.setMessageUpdateFetchState(FetchState.Loading);
            return advisorService.updateMessage(request)
                .then(response => {
                    this.updateMessages(response.data);
                    switch (actionType) {
                        case MessageActionType.Save: {
                            if (request[0].isSaved) {
                                notifierStore.setSuccess("Successfully saved the message(s).", 5000);
                            } else {
                                notifierStore.setSuccess("Successfully unsaved the message(s).", 5000);
                            }
                            break;
                        }
                        case MessageActionType.Archive: {
                            if (request[0].isArchived) {
                                notifierStore.setSuccess("Successfully archived the message(s).", 5000);
                            } else {
                                notifierStore.setSuccess("Successfully unarchived the message(s).", 5000);
                            }
                            break;
                        }
                    }
        })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        markMessageAsRead (id: string, isRead: boolean): Promise<any> {
            const notifierStore = useNotifierStore();
            this.setMessageUpdateFetchState(FetchState.Loading);
            return advisorService.markMessageReadValue(id, isRead)
                .then(response => {
                    this.updateMessage(response.data);

                    if (isRead) {
                        notifierStore.setSuccess("Successfully marked the message as read.", 5000);
                    }
                    else {
                        notifierStore.setSuccess("Successfully marked the message as unread.", 5000);
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});